/* eslint-disable @lwc/lwc/no-document-query */
import { createStore, selectParams } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { FORCE_COPILOT_LISTING_TYPE } from '~/constants';
import { IS_DEVELOPMENT, SALESFORCE_ORG_62_ACCOUNT_ID } from '~/modules/config';
import { generateLegacyListingUrl } from '~/modules/helpers/routingHelper';
import { isSecurityReviewRequiredListingError, ListingRequestOptions } from '~/modules/services/listingService';
import { ReviewSummary } from '~/modules/services/reviewService';
import { recentItemStore } from '~/modules/stores/recentItemStore';
import { routerStore } from '~/modules/stores/routerStore';
import { getCookie } from '~/modules/utilities/cookies';
import { logger, throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
import { ERROR_CODES, handleException } from '~/modules/utilities/error_utilities';
import { notFoundError, renderErrorInPlace } from '~/modules/utilities/errorUtilities';
import { isListingMatchingId, isSupportedTechnologyType, isValidListingId } from '~/modules/utilities/listing_utils';
import { ListingResponse, listingService } from '../services/listingService';

const APP_LISTINGS_TECHNOLOGIES = [
    'force/package',
    'force/bolt',
    'force/flow',
    'force/lightning',
    'quip/package',
    FORCE_COPILOT_LISTING_TYPE,
];

export interface ListingState extends State {
    // TODO: Enforce a maximum cache size for the map, 128 seems like a reasonable number of listings to cache
    listings: Map<string, ListingResponse>;
    isLoading: boolean;
}

const initialState: ListingState = {
    listings: new Map<string, ListingResponse>(),
    isLoading: false,
};

export const selectListing = (state: ListingState, listingId: string): ListingResponse | null => {
    // Guard against empty string lookups to prevent false matches
    if (!listingId) {
        return null;
    }

    const directMatch = state.listings.get(listingId);
    if (directMatch) {
        return directMatch;
    }

    // Fallback: check all cached listings for any matching ID format
    // Handles cases where listing is cached under a different ID format
    // (e.g., cached as OAF ID but looking up by appExchangeId and vice versa)
    for (const listing of state.listings.values()) {
        if (listing && isListingMatchingId(listing, listingId)) {
            return listing;
        }
    }

    return null;
};

export const selectListingFromUrl = (state: ListingState): ListingResponse | null => {
    const listingId = selectParams(routerStore.getState())?.listingId;
    if (isValidListingId(listingId)) {
        return selectListing(state, listingId);
    }
    return null;
};

export const selectListings = (state: ListingState, listingIds: string[]): ListingResponse[] => {
    if (listingIds) {
        return listingIds.map((listingId) => selectListing(state, listingId)).filter(Boolean) as ListingResponse[];
    }
    return [];
};

export function selectIsLinkListing(listing: ListingResponse): boolean {
    return listing.technology === 'b2c-commerce/package' || listing.technology === 'b2c-commerce/api';
}

export function selectIsSalesforceProduct(listing: ListingResponse): boolean {
    return listing.partnerLookup === SALESFORCE_ORG_62_ACCOUNT_ID;
}

export function selectIsAppListing(listing: ListingResponse): boolean {
    return APP_LISTINGS_TECHNOLOGIES.includes(listing.technology);
}

export const listingStore = createStore(
    // State
    initialState,
    // Action functions
    (set: SetState<ListingState>, get: GetState<ListingState>) => ({
        async fetchListing(id: string, options?: ListingRequestOptions) {
            if (!id) {
                throwDevelopmentOrLog(new Error(`Missing required id parameter: ${id}`));
                return;
            }

            const existingListing = selectListing(get(), id);
            if (existingListing) {
                return;
            }

            try {
                set(function setIsLoading(draft) {
                    draft.isLoading = true;
                });

                const { listing } = await listingService.getListing(
                    id,
                    getCookie('oaf_canary'),
                    undefined,
                    getCookie('appx_preview'),
                    options,
                );
                set(function setListing(draft: ListingState) {
                    draft.isLoading = false;
                    draft.listings.set(id, listing);
                });
            } catch (error) {
                // ListingService already records the expected restricted-listing denial as a
                // structured warning. Keep the legacy console warning for other failures only.
                if (options?.logErrors !== false && !isSecurityReviewRequiredListingError(error)) {
                    console.warn(`Failed to find listing with ID '${id}' in OAF`);
                }
                set(function updateListings(draft: ListingState) {
                    draft.isLoading = false;
                });
                throw error;
            }
        },
        upsertListing(listingId: string, listing: ListingResponse) {
            set(function upsertListing(draft: ListingState) {
                draft.listings.set(listingId, listing);
            });
        },
        updateReviewSummary(id: string, summary: ReviewSummary) {
            set(function updateReviewSummary(draft) {
                const listing = draft.listings.get(id);
                // @ts-expect-error * Automated Type Error Fix
                listing.reviewsSummary.reviewCount = summary.reviewCount;
                // @ts-expect-error * Automated Type Error Fix
                listing.reviewsSummary.averageRating = summary.averageRating;
            });
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    // Metadata
    'listingStore',
    IS_DEVELOPMENT,
);

export async function setOrFetchListing(
    listingId = '',
    context: string,
    isCurrentRequest: () => boolean = () => true,
): Promise<any> {
    try {
        if (!isValidListingId(listingId)) {
            notFoundError(context, isCurrentRequest);
            return false;
        }

        let listing = selectListing(listingStore.getState(), listingId);

        if (!listing) {
            await listingStore.fetchListing(listingId, { logErrors: false });
            if (!isCurrentRequest()) {
                return false;
            }
            listing = selectListing(listingStore.getState(), listingId);
            if (!listing) {
                throw new Error();
            }
        }

        if (!isCurrentRequest()) {
            return false;
        }

        if (!isSupportedTechnologyType(listing)) {
            const url = generateLegacyListingUrl(listing);
            logger.warn(
                `Attempt to access unsupported listing with ID '${listingId}' and Technology '${listing.technology}', redirecting to legacy page: ${url}`,
            );
            window.location.assign(url);
            return false;
        }

        if (listing.public) {
            recentItemStore.updateRecentItems('test-user-id', {
                listingId: listing.oafId ?? listing.external_id ?? '',
                timestamp: Date.now(),
            });
        }

        return listing;
    } catch (error) {
        if (isSecurityReviewRequiredListingError(error)) {
            if (!isCurrentRequest()) {
                return false;
            }
            handleException(error, 'listingStore.setOrFetchListing', {
                debugData: { listingId },
                level: 'warn',
            });
            await renderErrorInPlace(ERROR_CODES.SECURITY_REVIEW_REQUIRED, isCurrentRequest);
            return false;
        }
        if (!isCurrentRequest()) {
            return false;
        }
        const url = generateLegacyListingUrl();
        handleException(error, 'listingStore.setOrFetchListing', { debugData: { listingId, url } });
        window.location.assign(url);
    }
    return false;
}
